/**
 * Component: RagIndexStatus
 * Displays RAG indexing status information
 */

import React, { useEffect } from 'react';
import { useTranslation } from '../context/TranslationContext';

export default function RagIndexStatus({ status, world }) {
  const { t, loadPageTranslations } = useTranslation();

  useEffect(() => {
    loadPageTranslations('CHATBOT');
    loadPageTranslations('ADMIN');
  }, [loadPageTranslations]);

  if (!status) {
    return null;
  }

  // Format date helper
  const formatPhaseDate = (dateString) => {
    if (!dateString) return t('CHATBOT', 'NEVER', 'Never');
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return t('CHATBOT', 'NEVER', 'Never');
    }
  };

  // Si on a un message CLI (Vectorize info not available via API)
  if (status.vectorize_cli) {
    return (
      <div className="flex flex-col gap-3">
        {world && (
          <div className="flex items-center gap-2 text-sm border-b pb-2">
            <span className="text-gray-600">{t('ADMIN', 'CURRENT_WORLD', 'Monde actuel')}:</span>
            <strong className="text-gray-900">{world.title || world.name}</strong>
          </div>
        )}
        
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-600">{t('CHATBOT', 'INDEXED_ARTICLES', 'Indexed articles')}:</span>
            <span className="font-semibold text-gray-900">
              {status.total_articles_indexed || 0}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-600">{t('CHATBOT', 'LAST_INDEXED', 'Last indexed')}:</span>
            <span className="font-semibold text-gray-900">{formatPhaseDate(status.last_indexed_at)}</span>
          </div>

          {status.indexing_in_progress && (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
              <span className="text-blue-600 font-medium">{t('CHATBOT', 'INDEXING', 'Indexing...')}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Standard status display with 3-phase workflow
  return (
    <div className="flex flex-col gap-6">
      {world && (
        <div className="flex items-center gap-2 text-sm border-b pb-2">
          <span className="text-gray-600">{t('ADMIN', 'CURRENT_WORLD', 'Monde actuel')}:</span>
          <strong className="text-gray-900">{world.title || world.name}</strong>
        </div>
      )}
      
      {/* 4 Vertical Blocks Layout - Side by side */}
      <div className="grid grid-cols-4 gap-4">
        
        {/* Phase 1: Identification */}
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="text-sm font-semibold text-gray-800 mb-3">Phase 1: Identification</div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-gray-600 flex-1 text-xs">Dernière exécution:</span>
            <span className="font-semibold text-gray-700 text-xs">
              {formatPhaseDate(status.identify?.lastExecutedAt)}
            </span>
          </div>
          <div className="flex flex-col gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-600 flex-1">Total identifiés:</span>
              <span className="font-semibold text-gray-900">
                {status.identify?.total || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Phase 2: Preparation */}
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="text-sm font-semibold text-gray-800 mb-3">Phase 2: Préparation</div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-gray-600 flex-1 text-xs">Dernière exécution:</span>
            <span className="font-semibold text-gray-700 text-xs">
              {formatPhaseDate(status.prep?.lastExecutedAt)}
            </span>
          </div>
          <div className="flex flex-col gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-600 flex-1">En attente:</span>
              <span className="font-semibold text-gray-900">
                {status.prep?.pending || 0}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 flex-1">Préparés:</span>
              <span className="font-semibold text-blue-600">
                {status.prep?.prepared || 0}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 flex-1">Erreurs:</span>
              <span className="font-semibold text-red-600">
                {status.prep?.failed || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Phase 3: Indexing */}
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="text-sm font-semibold text-gray-800 mb-3">Phase 3: Indexation RAG</div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-gray-600 flex-1 text-xs">Dernière exécution:</span>
            <span className="font-semibold text-gray-700 text-xs">
              {formatPhaseDate(status.index?.lastExecutedAt)}
            </span>
          </div>
          <div className="flex flex-col gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-600 flex-1">En attente:</span>
              <span className="font-semibold text-gray-900">
                {status.index?.pending || 0}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 flex-1">Indexés:</span>
              <span className="font-semibold text-green-600">
                {status.index?.indexed || 0}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 flex-1">Total chunks:</span>
              <span className="font-semibold text-gray-900">
                {status.chunksCount || 0}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 flex-1">Erreurs:</span>
              <span className="font-semibold text-red-600">
                {status.index?.failed || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Phase 3e: Export */}
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="text-sm font-semibold text-gray-800 mb-3">Phase 3e: Export</div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-gray-600 flex-1 text-xs">Dernière exécution:</span>
            <span className="font-semibold text-gray-700 text-xs">
              {formatPhaseDate(status.export?.lastExecutedAt)}
            </span>
          </div>
          <div className="flex flex-col gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-600 flex-1">En attente:</span>
              <span className="font-semibold text-gray-900">
                {status.export?.pending || 0}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 flex-1">Exportés:</span>
              <span className="font-semibold text-purple-600">
                {status.export?.exported || 0}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 flex-1">Erreurs:</span>
              <span className="font-semibold text-red-600">
                {status.export?.failed || 0}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
